import { ERROR_MESSAGES } from '@/lib/dify/errorUtils';

/**
 * Connect with Facebook for data access
 * @param redirectTo - The URL to redirect to after connecting
 */
export async function connectWithFacebook(redirectTo?: string): Promise<void> {
  const url = `/api/facebook/connect${redirectTo ? `?redirectTo=${encodeURIComponent(redirectTo)}` : ''}`;
  window.location.href = url;
}

/**
 * Connect with Instagram using direct Instagram Login
 * @param redirectTo - The URL to redirect to after connecting
 */
export async function connectWithInstagram(redirectTo?: string): Promise<void> {
  const url = `/api/instagram/connect${redirectTo ? `?redirectTo=${encodeURIComponent(redirectTo)}` : ''}`;
  window.location.href = url;
}

/**
 * Connect with Threads
 * @param redirectTo - The URL to redirect to after connecting
 */
export async function connectWithThreads(redirectTo?: string): Promise<void> {
  const url = `/api/threads/connect${redirectTo ? `?redirectTo=${encodeURIComponent(redirectTo)}` : ''}`;
  window.location.href = url;
}

/**
 * Connect with TikTok
 * @param redirectTo - The URL to redirect to after connecting
 */
export async function connectWithTikTok(redirectTo?: string): Promise<void> {
  const url = `/api/tiktok/connect${redirectTo ? `?redirectTo=${encodeURIComponent(redirectTo)}` : ''}`;
  window.location.href = url;
}

/**
 * Connect with TikTok Business Account
 * @param redirectTo - The URL to redirect to after connecting
 */
export async function connectWithTikTokBusiness(redirectTo?: string): Promise<void> {
  const url = `/api/tiktok-business/connect${redirectTo ? `?redirectTo=${encodeURIComponent(redirectTo)}` : ''}`;
  window.location.href = url;
}

/**
 * Connect with YouTube
 * @param redirectTo - The URL to redirect to after connecting
 */
export async function connectWithYouTube(redirectTo?: string): Promise<void> {
  const url = `/api/youtube/connect${redirectTo ? `?redirectTo=${encodeURIComponent(redirectTo)}` : ''}`;
  window.location.href = url;
}

/**
 * Check Instagram connection status (for Instagram API with Facebook Login)
 */
export async function checkInstagramConnection() {
  try {
    const response = await fetch('/api/social/accounts');

    if (!response.ok) {
      throw new Error(ERROR_MESSAGES.SOCIAL.FETCH_ACCOUNTS_FAILED);
    }

    const data = await response.json();
    return {
      isConnected: Array.isArray(data.accounts) && data.accounts.length > 0,
      accounts: data.accounts || []
    };
  } catch (error) {
    console.error('Error checking Instagram connection:', error);
    return {
      isConnected: false,
      accounts: [],
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

/**
 * Verify if token exchange succeeded by checking the user's social connections
 * @returns Object containing verification results
 */
export async function verifyTokenExchange() {
  try {
    const response = await fetch('/api/user/verify-token-exchange');

    if (!response.ok) {
      throw new Error(ERROR_MESSAGES.SOCIAL.TOKEN_EXCHANGE_FAILED);
    }

    const data = await response.json();
    return {
      success: true,
      data,
      facebookConnected: data.facebook?.connected ?? false,
      instagramDirectConnected: data.instagram_direct?.connected ?? false,
      instagramViaFacebookConnected: data.instagram_via_facebook?.connected ?? false,
      timestamp: new Date().toISOString()
    };
  } catch (error) {
    console.error('Error verifying token exchange:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
      facebookConnected: false,
      instagramDirectConnected: false,
      instagramViaFacebookConnected: false
    };
  }
}

/**
 * Get all social connections for the current user
 */
export async function getSocialConnections() {
  try {
    const response = await fetch('/api/user/social-connections');

    if (!response.ok) {
      throw new Error(ERROR_MESSAGES.SOCIAL.FETCH_ACCOUNTS_FAILED);
    }

    const connections = await response.json();
    return {
      success: true,
      connections
    };
  } catch (error) {
    console.error('Error fetching social connections:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
      connections: []
    };
  }
}

/**
 * Remove a social connection
 * @param connectionId - The ID of the connection to remove
 */
export async function removeSocialConnection(connectionId: string) {
  try {
    const response = await fetch(`/api/user/social-connections/${connectionId}`, {
      method: 'DELETE'
    });

    if (!response.ok) {
      throw new Error(ERROR_MESSAGES.SOCIAL.DISCONNECT_FAILED);
    }

    return {
      success: true
    };
  } catch (error) {
    console.error('Error removing social connection:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
} 